import { DialogContent } from '@mui/material'
import { useMfrrDeliveredBids } from 'api/mfrr/mfrr.api'
import type { EnabledMfrrProductionUnit, MfrrMarket } from 'api/mfrr/mfrr.types'
import { formatDateTimeToLocalizedString } from 'helpers/dateTime.helper/dateTime.helper'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'ui/components/Dialog/Dialog'
import CopyDialogTitle from 'ui/molecules/CopyDialogTitle/CopyDialogTitle'
import { MfrrDeliveredBidsTable } from './MfrrDeliveredBidsTable'
import { getLatestMfrrDeliveredAt } from './MfrrLastDeliveredStatus.helper'

type MfrrLastDeliveredStatusProps = {
  systemId: number | undefined
  market: MfrrMarket
  units: EnabledMfrrProductionUnit[]
  dateStartTime: ISODateTime
  dateEndTime: ISODateTime
  isDeadlinePassed: boolean
}

export function MfrrLastDeliveredStatus({
  systemId,
  market,
  units,
  dateStartTime,
  dateEndTime,
  isDeadlinePassed,
}: MfrrLastDeliveredStatusProps): ReactNode {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {
    data: deliveredBids = [],
    isPending,
    isError,
  } = useMfrrDeliveredBids({
    systemId,
    market,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })

  if (isPending || isError) {
    return null
  }

  const latestDeliveredAt = getLatestMfrrDeliveredAt(deliveredBids)

  if (deliveredBids.length === 0) {
    // Once the whole day's deadline has passed, "no bids yet" no longer applies.
    if (isDeadlinePassed) {
      return null
    }

    return (
      <div className="pr-4 pt-2 text-right">
        <span className="text-sm italic text-grey-5">{t('No bids have been delivered yet')}</span>
      </div>
    )
  }

  return (
    <>
      <div className="pr-4 pt-2 text-right">
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          className="cursor-pointer border-0 bg-transparent p-0 text-sm italic text-grey-5 underline"
        >
          {latestDeliveredAt
            ? t('Last delivery {{time}}', { time: formatDateTimeToLocalizedString(latestDeliveredAt, true) })
            : t('View delivered bids')}
        </button>
      </div>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="lg">
        <CopyDialogTitle title={t('Delivered bids')} handleClose={() => setIsDialogOpen(false)} />
        <DialogContent>
          <MfrrDeliveredBidsTable deliveredBids={deliveredBids} units={units} market={market} />
        </DialogContent>
      </Dialog>
    </>
  )
}
